import React from 'react';
import { useAppSelector } from 'flow/hooks/useRedux';
import { twMerge } from 'tailwind-merge';
import useAppContext from 'flow/hooks/useAppContext';
import { selectTicketState } from 'flow/selectors/TicketsSelectors';
import { selectOwnUserHasTicketPermission } from 'flow/selectors/ticket-permission-selectors';
import { TicketKey } from 'flow/types/EntityKeys';

import Icon from 'flow/components/ui-components/Icon';
import EditableStatusChip from './EditableStatusChip';
import { CHIP_BASE_CLASSES, getChipBackplateClass } from './chip-classes';

type Props = {
    ticketKey: TicketKey;
    isSelected: boolean;
};

const StatusChip = ({ ticketKey, isSelected }: Props) => {
    const { i18next } = useAppContext();

    const ticketStatus = useAppSelector((state) =>
        selectTicketState(state, ticketKey),
    );
    const canSetState = useAppSelector((state) =>
        selectOwnUserHasTicketPermission(state, ticketKey, 'setState'),
    );

    const isClosed = ticketStatus === 'closed';

    // Closed tickets are de-emphasised: a gray chip with mid-gray text and
    // icon (rather than the previous solid-green "done" chip).
    const chipClassName = twMerge(
        CHIP_BASE_CLASSES,
        getChipBackplateClass(isSelected),
        isClosed ? 'text-gray-500' : null,
    );

    const content = (
        <>
            <Icon
                iconKey={
                    isClosed
                        ? 'assignmentCompleteOutlined'
                        : 'assignmentOutlined'
                }
                size="1rem"
                className={isClosed ? 'text-gray-500' : 'text-gray-600'}
            />
            <span>
                {isClosed
                    ? i18next.t('ticket.status.closed_singular')
                    : i18next.t('ticket.status.open_singular')}
            </span>
        </>
    );

    if (!canSetState) {
        return <div className={chipClassName}>{content}</div>;
    }

    return (
        <EditableStatusChip ticketKey={ticketKey} chipClassName={chipClassName}>
            {content}
        </EditableStatusChip>
    );
};

export default React.memo(StatusChip);
